import React, { useEffect, useMemo, useRef } from 'react';
import { filter, isEmpty, find, get, chain, uniqBy, throttle } from 'lodash-es';
import classNames from 'classnames';
import {
  Bubble,
  Button,
  Card,
  Icon,
  Justify,
  Layout,
  TextArea,
} from 'tea-component';
import { useAigcIntl } from '@/pages/aigc/utils/utils';
import { useMemoizedFn, useMount, useSafeState, useSetState, useUpdate } from 'ahooks';
import { renderImgText } from '@/pages/intelligence/components/TeaSelector/MultipleSelector';
import TipContext from '@/pages/home/components/TipContext';
import { useGetLangMap, useGetToTranslate } from './hooks';
import { GptDropdown } from './components/GptDropdown';
import { extractAndDistinctLangMaps } from './utils';
import './index.scss';
import Loading from '@/pages/intelligence/components/Loading';
import { useClipboard } from './hooks/useClipboard';
import TranslatorSelect from './components/TranslatorSelect';
import { buriedPointPage } from '@/pages/home/utils/buriedPoint';

const { Content } = Layout;
const MAX_LENGTH = 5000;
const optionDropdown = [
  { value: 'gpt_translate', text: 'GPT翻译' },
  { value: 'google_translate', text: 'Google translate' },
  { value: 'gpt4_translate', text: 'GPT4' },
];
enum LangKey {
  sourceKey = 1,
  targetKey = 2,
}
// eslint-disable-next-line react/display-name
function GPTTranslator() {
  // i18n
  const { formatMessage, isEn } = useAigcIntl('gptTranslator');
  // 下拉以及语言集合
  const [optionsValue, setOptionsValue] = useSetState<{
    [key in string]: unknown;
  }>({
    optionsGameValue: 'General',
    optionDropdown: ['gpt_translate', 'google_translate', 'gpt4_translate'],
    sourceTranslator: '',
    targetTranslator: '',
    // 区分是左边还是右边点击 1为左侧，2为右侧
    isDiffLangKeys: LangKey.sourceKey,
  });
  // 底部高亮
  const isActive = useRef('');
  // 输入框
  const [text, setText] = useSafeState('');
  // 业务列表
  const { data = [] } = useGetLangMap(
    useMemo(() => (isEn ? 'en' : 'zh'), [isEn]),
  );
  // 翻译列表
  const { data: list, run, cancel, loading, mutate } = useGetToTranslate();
  // 复制
  const [, { copyToClipboard }] = useClipboard();
  // 边框
  const isBorderActive = useRef({
    isHovered: false,
    isFocused: false,
    isDisabled: false,
  });
  const updata = useUpdate();

  const renderOptions = useMemo(
    () =>
      data?.map((item) => ({
        value: item?.game_code,
        text: renderImgText(item?.game, item?.img),
      })),
    [data],
  );
  const renderTranslatorOptions = useMemo(() => {
    // 如果是默认值，取所有语言并集，反之，取各自语言的数据
    const isDefaulet = optionsValue.optionsGameValue === 'General';
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { default_source, default_target } =
      find(data, {
        game_code: optionsValue.optionsGameValue,
      }) || {};
    const res = get(
      find(data, {
        game_code: isDefaulet ? '' : optionsValue.optionsGameValue,
      }),
      'lang_map',
      isDefaulet ? extractAndDistinctLangMaps(data) : [],
    );
    // 默认文案，根据业务找出code，在根据code找出第一项默认源语言，在根据第一项默认源语言找出默认目标语言
    const defauletTextList = get(
      find(data, {
        game_code: optionsValue.optionsGameValue,
      }),
      'demo',
      [],
    );
    // 第一次需求变更：如果点击一，就取右侧默认的文案，如果点击二，就取右侧的文案，每一次的变化，都会轮询一次
    // 如果当前右侧的值和默认值相同，取本身，反之，取他们对应的默认语言
    const diffTextKey =
      default_target === optionsValue.targetTranslator
        ? default_target
        : optionsValue.targetTranslator || default_target;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const default_text = get(
      find(defauletTextList, {
        // 根据源对象数据来查找
        key: optionsValue.sourceTranslator || default_source,
      }),
      'value',
      '',
    )[diffTextKey];
    const source = uniqBy(
      res.map((item) => ({
        value: item?.source,
        text: item?.source,
      })),
      'value',
    );
    const target = chain(res)
      .filter((item) => {
        if (isDefaulet) {
          return true;
        }
        return (
          item.source === (optionsValue.sourceTranslator || default_source)
        );
      })
      .map((item) => ({
        value: item?.target,
        text: item?.target,
      }))
      .uniqBy('value')
      .value();
    return {
      source,
      target,
      default_source,
      default_target,
      default_text,
    };
  }, [
    data,
    optionsValue.optionsGameValue,
    optionsValue.sourceTranslator,
    optionsValue.targetTranslator,
    optionsValue.isDiffLangKeys,
  ]);

  useEffect(() => {
    if (loading) {
      isBorderActive.current.isDisabled = true;
      updata();
      return;
    }
    isBorderActive.current.isDisabled = false;
    updata();
  }, [loading, updata]);

  useMount(()=>{
    buriedPointPage({
      pageId: '700901',
      pageName: 'GPTTranslator',
      extInfo: '',
      pagePath: '/GPTTranslator',
      typeId: 'aigc',
    });
  });

  useEffect(() => {
    if (renderTranslatorOptions.default_text) {
      isBorderActive.current.isDisabled = true;
      setText(renderTranslatorOptions.default_text);
      return;
    }
    isBorderActive.current.isDisabled = false;
    setText('');
  }, [renderTranslatorOptions.default_text, setText, updata]);

  useEffect(
    () => () => {
      mutate({});
      setText('');
    },
    [mutate],
  );

  const handleOnClickTextArea = () => {
    if (isBorderActive.current.isDisabled && !loading) {
      isBorderActive.current.isDisabled = false;
      setText('');
    }
  };

  const samplesBtn = [
    formatMessage('demo_0', {
      game_come: optionsValue.optionsGameValue,
      src:
        optionsValue.sourceTranslator ||
        renderTranslatorOptions?.default_source,
      trg:
        optionsValue.targetTranslator ||
        renderTranslatorOptions?.default_target,
    }),
  ];

  const renderTranslatorList = useMemo(() => {
    if (isEmpty(list)) return [];
    const config: { [key in string]: string } = {
      gpt_translate_text: formatMessage('title'),
      google_translate_text: 'Google Translate',
      gpt4_translate_text: 'GPT4',
    } as const;
    const res = Object.keys(config).map((task) => ({
      text: config?.[task],
      value: list?.[task],
    }));
    return filter(res, (item) => item.value !== '') || [];
  }, [formatMessage, list]);

  // 设置语言翻译
  const handleOnDropdownOptions = (value: string[]) => {
    setOptionsValue({
      optionDropdown: value,
    });
  };
  const handleOnChangeText = (value: string) => {
    if (value.length > MAX_LENGTH) return;
    setText(value);
  };

  // 提交
  const handleOnclickSubmit = throttle(() => {
    if (loading) {
      handleOnChangeStop();
      return;
    }
    const {
      optionsGameValue,
      optionDropdown,
      sourceTranslator,
      targetTranslator,
    } = optionsValue;
    run({
      game_code: optionsGameValue,
      src_lang: sourceTranslator || renderTranslatorOptions?.default_source,
      tgt_lang: targetTranslator || renderTranslatorOptions?.default_target,
      text,
      lang: isEn ? 'en' : 'zh',
      translate_tool: optionDropdown,
    });
  }, 800);

  // 取消
  const handleOnclickClear = () => {
    setText('');
    handleOnChangeStop();
    isActive.current = '';
    isBorderActive.current.isDisabled = false;
  };
  // 停止以及清空数据
  const handleOnChangeStop = () => {
    cancel();
    mutate([]);
  };
  // 语言切换
  const handleOnSelectTranslatorLanguge = <
    T extends unknown,
    S extends 'sourceTranslator' | 'targetTranslator',
  >(
    value: T,
    type: S,
  ): void => {
    // 根据code找出第一项lang数组，在通过计算出的第一项语言以此基础传入的value，找出第一个
    const oneKeyList = find(data, {
      game_code: optionsValue.optionsGameValue,
    })?.lang_map;
    const targetTranslator = find(oneKeyList, { source: value })?.target;
    setOptionsValue({
      targetTranslator,
      isDiffLangKeys:
        type === 'sourceTranslator'
          ? LangKey.sourceKey
          : LangKey.targetKey,
      [type]: value,
    });
    mutate({});
  };
  // 剪切
  const handleOnClickCopyText = async (value: string) => {
    await copyToClipboard(value);
  };
  // 底部默认文案事件回调
  const handleOnClickDefauletText = (task: string) => {
    // 如果用户没有输入,且有一个默认值，直接set默认值
    if (!text || isEmpty(renderTranslatorOptions.default_text)) {
      if (!isEmpty(renderTranslatorOptions.default_text)) {
        setText(renderTranslatorOptions.default_text);
      }
      // 反之，正常
    } else {
      isActive.current = task;
      isBorderActive.current.isDisabled = false;
    }

    updata();
  };
  // 用css控制Staet
  const handleFocus = () => {
    isBorderActive.current.isFocused = true;
    updata();
  };

  const handleBlur = () => {
    isBorderActive.current.isFocused = false;
    updata();
  };

  const handleMouseEnter = () => {
    isBorderActive.current.isHovered = true;
    updata();
  };

  const handleMouseLeave = () => {
    isBorderActive.current.isHovered = false;
    updata();
  };
  // 用css控制End
  return (
    <Content>
      <Content.Header title={formatMessage('title')} />
      <Content.Body full className={'dbi-gptTranslator-content-body'}>
        <Justify
          className="dbi-gptTranslator-justify"
          left={
            <>
              <TranslatorSelect
                label="dbi_label_genre"
                value={optionsValue.optionsGameValue}
                options={renderOptions}
                onConfirm={useMemoizedFn((optionsGameValue: string) => {
                  // 当这里变化的时候，把语言全部重置，并根据接口返回的默认项语言展示
                  setOptionsValue({
                    optionsGameValue,
                    sourceTranslator: '',
                    targetTranslator: '',
                    isDiffLangKeys: LangKey.sourceKey,
                  });
                  mutate({});
                })}
              />
              <TipContext
                AWidth={555}
                placement="top"
                tipText={formatMessage('tips')}
                className="tip-icon"
              />
            </>
          }
          right={
            <>
              <GptDropdown
                {...{
                  button: (
                    <div className="justify-setting">
                      <Icon type="setting" />
                    </div>
                  ),
                  type: 'checkout',
                  value: optionsValue.optionDropdown as string,
                  onChange: handleOnDropdownOptions,
                  style: {
                    marginRight: 0,
                  },
                  option: optionDropdown,
                }}
              />
            </>
          }
        />
        <Card className={'dbi-gptTranslator-card'}>
          <Card.Body style={{ padding: 0 }}>
            <div className="card-header">
              <div className="header-left">
                <GptDropdown
                  {...{
                    button:
                      optionsValue.sourceTranslator ||
                      renderTranslatorOptions?.default_source,
                    value:
                      optionsValue.sourceTranslator ||
                      renderTranslatorOptions?.default_source,
                    onChange: useMemoizedFn((e) =>
                      handleOnSelectTranslatorLanguge(e, 'sourceTranslator'),
                    ),
                    option: renderTranslatorOptions.source,
                    appearance: 'default',
                  }}
                />
              </div>

              <div className="header-right">
                {/* 语法切换。取消了 */}
                {/* <SvgIcon
                                    type="left-turn-right"
                                    style={{ marginRight: '15px', cursor: 'pointer' }}
                                    onClick={() => {
                                        setOptionsValue({
                                            sourceTranslator: optionsValue.targetTranslator || renderTranslatorOptions?.default_target,
                                            targetTranslator: optionsValue.sourceTranslator || renderTranslatorOptions?.default_source,
                                        });
                                    }}
                                /> */}
                <GptDropdown
                  {...{
                    button:
                      optionsValue.targetTranslator ||
                      renderTranslatorOptions?.default_target,
                    value:
                      optionsValue.targetTranslator ||
                      renderTranslatorOptions?.default_target,
                    onChange: useMemoizedFn((e) =>
                      handleOnSelectTranslatorLanguge(e, 'targetTranslator'),
                    ),
                    option: renderTranslatorOptions.target,
                    appearance: 'default',
                  }}
                />
              </div>
            </div>
            <div className="card-container">
              <div
                className={classNames('container-left', {
                  focused: loading
                    ? ''
                    : isBorderActive.current.isFocused ||
                      isBorderActive.current.isHovered,
                })}
              >
                <TextArea
                  size="full"
                  readonly={isBorderActive.current.isDisabled}
                  value={text}
                  onChange={handleOnChangeText}
                  placeholder={formatMessage('input_plearhost')}
                  onPressEnter={handleOnclickSubmit}
                  // TOOD:加入这2个属性，打字会不连贯，还未排查到原因，先临时创建一个div
                  // maxLength={5000}
                  // showCount={!!text?.length}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  onClick={handleOnClickTextArea}
                  className={classNames({
                    disableds: isBorderActive.current.isDisabled,
                  })}
                />
                {text?.length > 0 && (
                  <div className="card-container-btn">
                    <>
                      <Button
                        style={{ marginRight: '10px' }}
                        type={loading ? 'error' : 'primary'}
                        onClick={handleOnclickSubmit}
                      >
                        {loading
                          ? formatMessage('stop')
                          : formatMessage('translate')}
                      </Button>
                      <Button type="weak" onClick={handleOnclickClear}>
                        {formatMessage('empty')}
                      </Button>
                      <div
                        className={classNames('card-container-length', {
                          'card-container-length-error':
                            text?.length >= MAX_LENGTH,
                        })}
                      >
                        {text?.length}/{MAX_LENGTH}
                      </div>
                    </>
                  </div>
                )}
              </div>
              <div className="container-right">
                {loading ? (
                  // Loading 状态展示区域
                  <div style={{ position: 'relative', top: '50%' }}>
                    <Loading />
                  </div>
                ) : (
                  renderTranslatorList?.map((task) => (
                    <React.Fragment key={task.text}>
                      <div className="container-right-translator">
                        {task.text}
                      </div>
                      <div className="container-right-translator-list">
                        {task.value}
                        <div className="copy-icon">
                          <Bubble
                            content={formatMessage('copy_translation')}
                            dark
                          >
                            <Icon
                              type="copy"
                              onClick={() => { void handleOnClickCopyText(task.value) }}
                            />
                          </Bubble>
                        </div>
                      </div>
                    </React.Fragment>
                  ))
                )}
              </div>
            </div>
          </Card.Body>
        </Card>
        {/* 产品要求，没有默认文本，直接hidden */}
        {!isEmpty(renderTranslatorOptions.default_text) && (
          <Card className={'dbi-gptTranslator-samples'}>
            <Card.Body>
              <div className="samples-header">
                <span>{formatMessage('no_translate')} </span>{' '}
                {formatMessage('try_text')}
              </div>
              <div className="samples-container">
                {samplesBtn.map((task) => (
                  <Button
                    key={task}
                    className={classNames('samples-btn', {
                      'samples-btn-click': isActive.current === task,
                      disabled: isEmpty(renderTranslatorOptions.default_text),
                    })}
                    onClick={() => handleOnClickDefauletText(task)}
                  >
                    {task}
                  </Button>
                ))}
              </div>
            </Card.Body>
          </Card>
        )}
      </Content.Body>
    </Content>
  );
}

export default GPTTranslator;

